import { useEffect, useMemo, useState } from "react";
import "./Calendar.css";
import SearchInput from "../searchInput/SearchInput";
import { Popover } from "react-tiny-popover";
import {
  PaginationButton,
  PrimaryButton,
  SecondaryButton,
} from "../buttonComponent/ButtonComponent";
import TabSwitcher from "../tabSwitcher/TabSwitcher";
import MonthlyGrid from "./MonthlyGrid";
import WeeklyGrid from "./WeeklyGrid";
import MobileMonthlyList from "./MobileMonthlyList";
import LoadingSpinner from "../loading/LoadingSpinner";
import { useAuth } from "../../config/utilities/useAuth";
import { useIsMobile } from "../../config/utilities/useBreakpoint";
import PlusIcon from "../buttonComponent/img/create.svg";
import Text from "../text/Text";

const TRIP_TYPE_FILTER_COLORS = [
  { background: "#ECFDFF", color: "#06AED4" },
  { background: "#FEF6EE", color: "#B93815" },
  { background: "#F4F3FF", color: "#5925DC" },
  { background: "#E0F2FE", color: "#0C4A6E" },
  { background: "#FFFBEA", color: "#92400E" },
];

export default function Calendar({
  height,
  setAction,
  onSelectDate,
  data,
  selectedStatusFilter,
  setSelectedStatusFilter,
  loading,
  onRangeChange,
}) {
  const { privilege, tripTypes } = useAuth();
  const role = privilege?.role;
  const isMobileViewport = useIsMobile();
  const [showFilters, setShowFilters] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth > 768;
  });
  const [view, setView] = useState(() => {
    const stored = localStorage.getItem("calendar-view");
    const storedView = stored !== null ? JSON.parse(stored) : "monthly";
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      return "monthly";
    }
    return storedView;
  });
  const [date, setDate] = useState(new Date());
  const [tripSearch, setTripSearch] = useState("");
  const [filtersPopoverOpen, setFiltersPopoverOpen] = useState(false);
  const [showWeekends, setShowWeekends] = useState(() => {
    const stored = localStorage.getItem("calendar-showWeekends");
    return stored !== null ? JSON.parse(stored) : true;
  });

  useEffect(() => {
    localStorage.setItem("calendar-showWeekends", JSON.stringify(showWeekends));
  }, [showWeekends]);

  useEffect(() => {
    localStorage.setItem("calendar-view", JSON.stringify(view));
  }, [view]);

  useEffect(() => {
    if (isMobileViewport) {
      setShowFilters(false);
    } else {
      setShowFilters(true);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    // On mobile we assume "show everything" so weekends are always included.
    if (isMobileViewport) setShowWeekends(true);
  }, [isMobileViewport]);

  useEffect(() => {
    // Keep mobile calendar locked to monthly mode.
    if (isMobileViewport && view !== "monthly") {
      setView("monthly");
    }
  }, [isMobileViewport, view]);

  useEffect(() => {
    if (onRangeChange) {
      onRangeChange(date, view);
    }
  }, [date, view]);

  const changeMonth = (offset) => {
    const newDate = new Date(date);
    newDate.setMonth(date.getMonth() + offset);
    setDate(newDate);
  };

  const changeWeek = (offset) => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + offset * 7);
    setDate(newDate);
  };

  const handleAllTripTypes = () => {
    setSelectedStatusFilter([]);
  };

  const handleTripTypeToggle = (id) => {
    setSelectedStatusFilter((prev) => {
      if (!prev.length) {
        return [id];
      }
      const isRemoving = prev.includes(id);
      return isRemoving ? prev.filter((v) => v !== id) : [...prev, id];
    });
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const term = tripSearch.trim().toLowerCase();
    if (!term) return data;
    return data.filter((entry) =>
      entry?.tripDetails?.tripName?.toLowerCase()?.includes(term)
    );
  }, [data, tripSearch]);

  const isAllTripTypesFilter =
    !selectedStatusFilter || selectedStatusFilter.length === 0;

  const tripTypeFilterActiveCount = isAllTripTypesFilter
    ? 0
    : selectedStatusFilter.length;

  const filterPopoverContent = (
    <div className="calendar-filters-popover">
      <div className="calendar-filters-popover-header">
        <span className="calendar-filters-popover-title">Trip types</span>
        {!isAllTripTypesFilter && (
          <span className="calendar-filters-popover-summary" aria-live="polite">
            {tripTypeFilterActiveCount} selected
          </span>
        )}
      </div>
      <p className="calendar-filters-popover-hint">
        Choose &ldquo;All types&rdquo; to show every trip type, or pick one or
        more to narrow the calendar.
      </p>
      <div
        className="calendar-filter-pills calendar-filter-pills-popover"
        role="group"
        aria-label="Trip type filters"
      >
        <button
          type="button"
          className={`calendar-filter-pill calendar-filter-pill-all ${
            isAllTripTypesFilter ? "is-active" : ""
          }`}
          onClick={handleAllTripTypes}
        >
          All types
        </button>
        {tripTypes?.map((type, index) => {
          const { id, name } = type;
          const colors =
            TRIP_TYPE_FILTER_COLORS[index % TRIP_TYPE_FILTER_COLORS.length];
          const selected =
            !isAllTripTypesFilter && selectedStatusFilter.includes(id);
          return (
            <button
              key={id}
              type="button"
              className={`calendar-filter-pill calendar-filter-pill-trip ${
                selected ? "is-active" : ""
              }`}
              style={
                selected
                  ? {
                      background: colors.background,
                      color: colors.color,
                      borderColor: colors.color,
                    }
                  : undefined
              }
              onClick={() => handleTripTypeToggle(id)}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );

  const isTodayInView = () => {
    const today = new Date();
    if (view === "monthly") {
      return (
        today.getMonth() === date.getMonth() &&
        today.getFullYear() === date.getFullYear()
      );
    } else if (view === "weekly") {
      const startOfWeek = new Date(date);
      let dayOfWeek = date.getDay();
      if (dayOfWeek === 0) dayOfWeek = 7;
      startOfWeek.setDate(date.getDate() - (dayOfWeek - 1));

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return today >= startOfWeek && today <= endOfWeek;
    }

    return false;
  };

  return (
    <div className="calendar-container" style={{ height }}>
      <div className="calendar-header">
        <div className="calendar-header-left">
          <div className="calendar-search">
            <SearchInput
              containerWidth="100%"
              text="Search by trip name..."
              value={tripSearch}
              onChangeText={(e) => setTripSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="calendar-header-center">
          <div className="calendar-month-label">
            <Text
              text={`${date.toLocaleString("default", {
                month: "long",
              })} ${date.getFullYear()}`}
              size="1rem"
              weight="500"
              color="#101828"
            />
          </div>
        </div>
        <div className="calendar-header-right">
          {!isMobileViewport && (
            <SecondaryButton
              label="Today"
              width="auto"
              onClickFunction={() => {
                const today = new Date();
                setDate(today);
              }}
              disable={isTodayInView()}
            />
          )}
          {!isMobileViewport && showFilters ? (
            <div className="calendar-header-controls">
              <button
                type="button"
                className={`calendar-filter-weekend ${showWeekends ? "is-on" : ""}`}
                onClick={() => setShowWeekends(!showWeekends)}
                aria-pressed={showWeekends}
              >
                Weekends
              </button>
              <Popover
                isOpen={filtersPopoverOpen}
                positions={["bottom", "left", "right", "top"]}
                padding={8}
                onClickOutside={() => setFiltersPopoverOpen(false)}
                containerStyle={{ zIndex: 1000 }}
                content={filterPopoverContent}
              >
                <button
                  type="button"
                  className={`calendar-filters-trigger ${
                    !isAllTripTypesFilter ? "has-active-filters" : ""
                  } ${filtersPopoverOpen ? "is-open" : ""}`}
                  onClick={() => setFiltersPopoverOpen((o) => !o)}
                  aria-expanded={filtersPopoverOpen}
                  aria-haspopup="dialog"
                  aria-label={
                    !isAllTripTypesFilter
                      ? `Trip filters: ${tripTypeFilterActiveCount} type${
                          tripTypeFilterActiveCount === 1 ? "" : "s"
                        } selected. Open menu.`
                      : "Trip filters: all types. Open menu."
                  }
                >
                  <span className="calendar-filters-trigger-label">Filters</span>
                  {!isAllTripTypesFilter && (
                    <span className="calendar-filters-badge" aria-hidden="true">
                      {tripTypeFilterActiveCount}
                    </span>
                  )}
                </button>
              </Popover>
            </div>
          ) : null}

          {role !== "VIEWER" && (
            <PrimaryButton
              imageIcon={PlusIcon}
              label="Create Schedule"
              width="auto"
              onClickFunction={() => setAction("create")}
            />
          )}
        </div>
      </div>
      <div className="calendar-main">
        {loading ? (
          <div className="calendar-loading-container">
            <LoadingSpinner
              className="loading-spinner"
              size="large"
              color="#ffb502"
            />
          </div>
        ) : isMobileViewport ? (
          <MobileMonthlyList
            date={date}
            showWeekends={showWeekends}
            onSelectDay={onSelectDate}
            data={filteredData}
          />
        ) : view === "monthly" ? (
          <MonthlyGrid
            date={date}
            showWeekends={showWeekends}
            onSelectDay={onSelectDate}
            data={filteredData}
          />
        ) : (
          <WeeklyGrid
            date={date}
            showWeekends={showWeekends}
            onSelectDay={onSelectDate}
            data={filteredData}
          />
        )}
      </div>

      <div className="calendar-footer">
        {!isMobileViewport && (
          <div className="view-toggle">
            <TabSwitcher currentView={view} setCurrentView={setView} />
          </div>
        )}
        <div className="pagination-buttons">
          {view === "monthly" ? (
            <>
              <PaginationButton
                onClick={() => changeMonth(-1)}
                direction="previous"
              />
              <PaginationButton
                onClick={() => changeMonth(1)}
                direction="next"
              />
            </>
          ) : (
            <>
              <PaginationButton
                onClick={() => changeWeek(-1)}
                direction="previous"
              />
              <PaginationButton
                onClick={() => changeWeek(1)}
                direction="next"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
